import { useEffect, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Bell } from 'lucide-react'
import { api } from '../services/api'
import { NotificationsPanel, NOTIFICATIONS_QUERY_KEY } from './NotificationsPanel'

interface Props {
  /** Цвет иконки в обычном состоянии. */
  iconColor?: string
  /** Фон/обводка кнопки. */
  accent?: string
}

/**
 * Кнопка колокольчика с панелью уведомлений.
 * • Красная точка при unreadCount > 0.
 * • Однократная пульсация когда unreadCount УВЕЛИЧИЛСЯ (новое уведомление).
 * • Тап → открывает NotificationsPanel.
 */
export function NotificationsBell({ iconColor = '#4A6CF7', accent = '#4A6CF7' }: Props) {
  const { data } = useQuery({
    queryKey: NOTIFICATIONS_QUERY_KEY,
    queryFn: () => api.getNotifications({ limit: 50 }),
    staleTime: 30_000,
    refetchInterval: 60_000,
  })
  const unread = data?.unreadCount ?? 0
  const [open, setOpen] = useState(false)
  const [pulse, setPulse] = useState(false)
  const prevUnreadRef = useRef<number | null>(null)

  useEffect(() => {
    const prev = prevUnreadRef.current
    if (prev != null && unread > prev) {
      // Новое уведомление пришло — однократная пульсация.
      setPulse(true)
      const t = setTimeout(() => setPulse(false), 1800)
      return () => clearTimeout(t)
    }
    prevUnreadRef.current = unread
  }, [unread])

  return (
    <>
      <button
        data-testid="notifications-bell"
        onClick={() => setOpen(true)}
        aria-label="Уведомления"
        className={pulse ? 'notif-bell-pulse' : undefined}
        style={{
          width: 36, height: 36, borderRadius: 12, padding: 0,
          background: `${accent}14`,
          border: `1px solid ${accent}24`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          position: 'relative', flexShrink: 0,
          cursor: 'pointer',
          transition: 'background 0.15s, border-color 0.15s',
        }}
      >
        <Bell size={16} color={iconColor} />
        {unread > 0 && (
          <span data-testid="notifications-bell-dot" style={{
            position: 'absolute', top: 6, right: 6,
            width: 8, height: 8, borderRadius: '50%',
            background: '#FF3B30', border: '1.5px solid white',
            boxShadow: pulse ? '0 0 0 4px rgba(255,59,48,0.20)' : 'none',
          }} />
        )}
        <style>{`
          @keyframes notif-bell-pulse {
            0%   { transform: rotate(0deg);  }
            15%  { transform: rotate(14deg); }
            30%  { transform: rotate(-12deg); }
            45%  { transform: rotate(10deg); }
            60%  { transform: rotate(-8deg); }
            75%  { transform: rotate(5deg);  }
            100% { transform: rotate(0deg);  }
          }
          .notif-bell-pulse svg {
            animation: notif-bell-pulse 0.9s ease-in-out 1;
            transform-origin: 50% 0%;
          }
        `}</style>
      </button>
      <NotificationsPanel open={open} onClose={() => setOpen(false)} />
    </>
  )
}
